import { useState } from 'react';
import { Card, Stack, Group, Text, Button } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { ApiError } from '@repletoryreach/sdk-api';
import { useReferredBy, useApplyReferralCode } from '../../../data/useAffiliate';
import { useT } from '../../../i18n/I18nProvider';

/**
 * "Were you referred by a partner?"
 *
 * Shown on the billing page only while it can still be answered: a code may
 * be entered for a short while after signing up and only before the first
 * payment, because commission on money a partner had no hand in is not
 * theirs. Once a code is in, the card says who introduced the account and
 * offers nothing further.
 */
export function ReferredByCard() {
  const t = useT();
  const referral = useReferredBy();
  const apply = useApplyReferralCode();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const d = referral.data?.data;
  if (referral.isLoading || !d) return null;
  if (!d.referred && !d.eligible) return null;

  if (d.referred) {
    return (
      <Card withBorder>
        <Stack gap="xs">
          <Text role="label">{t('billing.referredBy.title')}</Text>
          <Text role="bodySmall" c="var(--rr-color-text-muted)">
            {t('billing.referredBy.body', { name: d.referred_by ?? '' })}
          </Text>
        </Stack>
      </Card>
    );
  }

  async function submit() {
    setError(null);
    try {
      await apply.mutateAsync(code.trim().toUpperCase().replace(/[\s-]/g, ''));
      notifySuccess(t('billing.referredBy.thanks'));
      setCode('');
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: { message?: string } } | null) : null;
      setError(body?.error?.message ?? t('billing.referredBy.failed'));
    }
  }

  return (
    <Card withBorder>
      <Stack gap="md">
        <Stack gap="xs">
          <Text role="label">{t('billing.referredBy.askTitle')}</Text>
          <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('billing.referredBy.askBody')}</Text>
        </Stack>
        {error && <Alert color="error" role="alert">{error}</Alert>}
        <Group align="flex-end" gap="sm">
          <TextInput
            label={t('billing.referredBy.codeLabel')}
            description={t('billing.referredBy.codeHint')}
            value={code}
            onChange={(e) => setCode(e.currentTarget.value)}
            maxLength={24}
            autoComplete="off"
          />
          <Button onClick={submit} loading={apply.isPending} disabled={code.trim().length < 4}>
            {t('billing.referredBy.submit')}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
